import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import type { EventDetailDTO, EventOptionDTO } from "../../shared/dto";
import type { BankAccount } from "../../shared/prepayment";
import { uploadsUrl } from "../lib/uploader";
import { formatEventDate, fmtDateTime } from "../lib/format";
import { RichTextRender } from "../components/RichTextRender";
import { UnivapayPayButtons } from "../components/UnivapayPayButtons";
import { PaypalPayButton } from "../components/PaypalPayButton";
import { TelecomPayButton } from "../components/TelecomPayButton";
import { PayjpPayButton, finishPayjpReturn } from "../components/PayjpPayButton";
import { StripePayButton, finishStripeReturn } from "../components/StripePayButton";
import {
  StatusBadge,
  Loading,
  ErrorBanner,
  SuccessBanner,
} from "../components/ui";

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  pending_payment: "決済待ち",
  applied: "申込中",
  cancelled: "キャンセル",
  checked_in: "受付済み",
  no_show: "不参加",
};
const APPLICATION_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  pending_payment: "warning",
  applied: "neutral",
  cancelled: "neutral",
  checked_in: "success",
  no_show: "danger",
};

const MODE_LABEL: Record<string, string> = {
  offline: "来場",
  online: "オンライン",
  hybrid: "ハイブリッド",
};

interface EventDetailResp extends EventDetailDTO {
  myApplication?: {
    id: string;
    applicationStatus: string;
    participationMode: string;
    appliedAt: string;
    paymentStatus?: string;
    withSocial?: boolean;
  } | null;
  remainingCapacity: number | null;
  hasSocial?: boolean;
  socialCapacity?: number | null;
  socialFee?: number;
  socialVenue?: string | null;
  socialStartAt?: string | null;
  socialAppliedCount?: number;
  socialRemainingCapacity?: number | null;
  cancelDeadlineAt?: string | null;
  canCancel?: boolean;
  /** 事前決済が必要か */
  prepaymentRequired?: boolean;
  /** 参加費。未設定なら null（表示と決済ボタンの金額に使う） */
  feeAmount?: number | null;
  /** 支払い方法。カードのときだけ決済ボタンを出す */
  paymentMethod?: "none" | "bank_transfer" | "card";
  /** オンライン決済のとき、鍵が入っていて実際に払える手段 */
  onlineProviders?: {
    univapay: boolean;
    paypal: boolean;
    telecom?: boolean;
    telecomBank?: boolean;
    payjp?: boolean;
    stripe?: boolean;
  } | null;
  /** 振込先。銀行振込のときだけサーバが返す（未登録なら null） */
  bankAccount?: BankAccount | null;
  /** 定員のうち有料会員のために確保している席数 */
  paidReservedSeats?: number;
}

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [data, setData] = useState<EventDetailResp | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"offline" | "online" | "hybrid">("offline");
  const [withSocial, setWithSocial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // 支払い画面から戻った後の反映の結果がまだ分からない支払い（通信の途中切れなど）。もう一度頼める
  const [returnRetry, setReturnRetry] = useState<{ provider: "payjp" | "stripe"; id: string } | null>(null);

  useEffect(() => {
    if (!eventId) return;
    api.get<EventDetailResp>(`/events/${eventId}`).then((d) => {
      setData(d);
      // 来場のみ/オンラインのみは固定。両方開催(hybrid)は既定を来場にして会員が選ぶ。
      setMode(d.participationMode === "online" ? "online" : "offline");
      const required = (d.options ?? [])
        .filter((o) => o.isRequired)
        .map((o) => o.id);
      setSelected(new Set(required));
    });
  }, [eventId]);

  /**
   * 支払い画面から戻った支払いの反映を頼み、結果を画面に出す（PAY.JP は確定、Stripe は反映）。
   * 結果が分からなければ ID を残して再試行できるようにする
   */
  const finishReturn = async (provider: "payjp" | "stripe", id: string) => {
    setErr(null);
    setReturnRetry(null);
    setMsg("お支払いを確認しています…");
    const r = provider === "payjp" ? await finishPayjpReturn(id) : await finishStripeReturn(id);
    if (r.ok) setMsg(r.message);
    else {
      setMsg(null);
      setErr(r.message);
      if (r.retry) setReturnRetry({ provider, id });
    }
    if (!eventId) return;
    try {
      setData(await api.get<EventDetailResp>(`/events/${eventId}`));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  // 決済画面から戻ってきた。
  //   PAY.JP … 本人認証（3Dセキュア）を終えた支払いを、ここでサーバに確定してもらう
  //   Stripe … 支払いページで確定済み。ここでサーバに取り直してもらう（Webhook でも届く）
  //   テレコム … 結果はテレコムからサーバへ別に届くので、少し待ってから一度だけ取り直す
  useEffect(() => {
    if (!eventId) return;
    const query = new URLSearchParams(window.location.search);
    const pay = query.get("pay");
    const charge = query.get("charge");
    if (pay === "payjp" && charge) {
      // PAY.JP の本人認証（3Dセキュア）から戻ってきた。確定はサーバが行う。
      // 再読み込みで同じ確定を繰り返さないよう、URL から外しておく（繰り返しても結果は同じ）
      window.history.replaceState(null, "", window.location.pathname);
      // URL から外した後なので、StrictMode の再実行でも確定は 1 回だけ。結果は必ず画面に出す
      void finishReturn("payjp", charge);
      return;
    }
    const session = query.get("session");
    if (pay === "stripe" && session) {
      // Stripe の支払いページから戻ってきた。URL から外してから反映を頼む（繰り返しても結果は同じ）
      window.history.replaceState(null, "", window.location.pathname);
      void finishReturn("stripe", session);
      return;
    }
    if (pay === "telecom-bank") {
      // 銀行振込は、入金されるまで支払い済みにならない（取り直しても変わらない）
      setMsg(
        "銀行振込のお申込みを受け付けました。振込先はテレコムクレジットからのメールをご確認ください。入金が確認できると自動で支払い済みになります。",
      );
      return;
    }
    if (pay !== "telecom") return;
    setMsg("お支払いを確認しています。反映まで少しかかることがあります。");
    const t = setTimeout(() => {
      api
        .get<EventDetailResp>(`/events/${eventId}`)
        .then(setData)
        .catch((e) => setErr((e as Error).message));
    }, 5000);
    return () => clearTimeout(t);
  }, [eventId]);

  if (!data) return <Loading />;

  const apply = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await api.post<{ ok: boolean; needsPayment: boolean }>(
        `/events/${data.id}/apply`,
        { participationMode: mode, optionIds: Array.from(selected), withSocial },
      );
      setMsg(
        r.needsPayment ? "決済画面に進んでください。" : "申込が完了しました。",
      );
      const d = await api.get<EventDetailResp>(`/events/${data.id}`);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  /** 決済が確定したら（サーバが確かめた後）表示を取り直す。 */
  const onPaid = async () => {
    setMsg("お支払いを確認しました。");
    try {
      setData(await api.get<EventDetailResp>(`/events/${data.id}`));
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const full = data.remainingCapacity === 0;
  const { date, time } = formatEventDate(data.eventStartAt);
  const thumb = uploadsUrl(data.thumbnailKey);

  return (
    <article className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/events"
        className="tap-target text-sm text-tora-ink/60 hover:text-tora"
      >
        ← イベント一覧へ
      </Link>

      <header className="space-y-3">
        <p className="text-[11px] text-tora-accent tracking-widest font-medium">
          EVENT
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-tora leading-tight">
          {data.title}
        </h2>
        {data.summary && (
          <p className="text-base md:text-lg text-tora-ink/70 leading-relaxed">
            {data.summary}
          </p>
        )}

        {/* meta row: 日時 (2 行) / モード / 会場 */}
        <div className="pt-2 space-y-1 text-sm">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-bold text-tora">{date}</span>
            {time && <span className="text-tora-ink/70">{time}</span>}
          </div>
          <p className="text-xs text-tora-ink/60">
            {MODE_LABEL[data.participationMode] ?? data.participationMode}
            {data.venueName ? ` / ${data.venueName}` : ""}
          </p>
        </div>

        {/* ステータスバッジ集約 */}
        <div className="flex flex-wrap gap-1.5">
          {data.myApplication && (
            <StatusBadge
              tone={
                APPLICATION_STATUS_TONE[data.myApplication.applicationStatus] ??
                "neutral"
              }
              bold={data.myApplication.applicationStatus === "checked_in"}
            >
              {APPLICATION_STATUS_LABELS[data.myApplication.applicationStatus] ??
                data.myApplication.applicationStatus}
            </StatusBadge>
          )}
          {data.prepaymentRequired && (
            <StatusBadge tone="warning" bold>
              事前決済
            </StatusBadge>
          )}
          {full ? (
            <StatusBadge tone="danger" bold>
              満席
            </StatusBadge>
          ) : data.remainingCapacity != null ? (
            <StatusBadge tone="neutral">
              残 {data.remainingCapacity} 席
              {/* 確保枠があるときは内訳も出す。無料会員が「残っているのに
                  申し込めない」理由が分からないままになるのを避ける。 */}
              {data.paidReservedSeats
                ? `（うち有料会員枠 ${data.paidReservedSeats} 席）`
                : ""}
            </StatusBadge>
          ) : null}
        </div>
      </header>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-black/10 bg-black/5 shadow-sm"
        />
      )}

      <RichTextRender html={data.description} />

      <section className="card p-5 md:p-6 border-l-4 border-l-tora-accent space-y-3">
        <h3 className="font-bold text-lg text-tora">申込</h3>

        {data.myApplication ? (
          <div className="space-y-3">
            <div className="text-sm text-tora-ink/80">
              申し込み済みです。
              {data.myApplication.withSocial && (
                <span className="ml-1 font-semibold text-tora">(懇親会参加)</span>
              )}
            </div>
            {/* オンライン決済（UnivaPay / PayPal / テレコムクレジット / PAY.JP / Stripe）。**未払いのときだけ**出す
                （キャンセルした申込には出さない。サーバも受け付けない）。
                鍵が入っている手段だけを出す（押して失敗するボタンを出さない）。
                支払えたかどうかはサーバが判定して反映する。 */}
            {data.paymentMethod === "card" &&
              data.myApplication.paymentStatus === "unpaid" &&
              data.myApplication.applicationStatus !== "cancelled" && (
                <div className="space-y-4">
                  {(data.onlineProviders?.univapay ?? true) && (
                    <UnivapayPayButtons
                      applicationId={data.myApplication.id}
                      onPaid={onPaid}
                    />
                  )}
                  {data.onlineProviders?.paypal && (
                    <PaypalPayButton
                      applicationId={data.myApplication.id}
                      onPaid={onPaid}
                    />
                  )}
                  {data.onlineProviders?.telecom && (
                    <TelecomPayButton applicationId={data.myApplication.id} />
                  )}
                  {data.onlineProviders?.telecom && data.onlineProviders.telecomBank && (
                    <TelecomPayButton applicationId={data.myApplication.id} kind="bank" />
                  )}
                  {data.onlineProviders?.payjp && (
                    <PayjpPayButton applicationId={data.myApplication.id} />
                  )}
                  {data.onlineProviders?.stripe && (
                    <StripePayButton applicationId={data.myApplication.id} />
                  )}
                </div>
              )}
            {/* 振込（自前の口座）の事前決済。申し込んだ後も振込先が見えるように */}
            {data.prepaymentRequired &&
              data.paymentMethod !== "card" &&
              data.myApplication.applicationStatus !== "cancelled" && (
                <div className="rounded-lg border border-amber-400/60 bg-amber-50/50 p-4 space-y-2">
                  <BankTransferInfo account={data.bankAccount ?? null} lead="下記へお振り込みください。" />
                </div>
              )}
            {data.canCancel && ["pending_payment", "applied"].includes(data.myApplication.applicationStatus) && (
              <button
                className="btn-outline text-sm"
                disabled={busy}
                onClick={async () => {
                  // 支払い済みの申込は、キャンセルしても自動では返金しない（運営が手動で返金する決まり）
                  const paid = data.myApplication?.paymentStatus === "paid";
                  if (
                    !confirm(
                      paid
                        ? "この申込をキャンセルしますか？\nお支払い済みの参加費は自動では返金されません。返金は運営からご連絡します。\n返金が済むまで、このイベントには申し込み直せません。"
                        : "この申込をキャンセルしますか？",
                    )
                  )
                    return;
                  setBusy(true);
                  setErr(null);
                  try {
                    await api.post(`/events/${data.id}/cancel`);
                    setMsg("キャンセルしました");
                    const d = await api.get<EventDetailResp>(`/events/${data.id}`);
                    setData(d);
                  } catch (e) {
                    setErr((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "処理中…" : "申込をキャンセル"}
              </button>
            )}
            {!data.canCancel && (
              <p className="text-xs text-tora-ink/50">キャンセル締切を過ぎています</p>
            )}
            {err && <ErrorBanner>{err}</ErrorBanner>}
            {returnRetry && (
              <button
                type="button"
                className="btn-outline text-sm"
                onClick={() => void finishReturn(returnRetry.provider, returnRetry.id)}
              >
                もう一度確認する
              </button>
            )}
            {msg && <SuccessBanner>{msg}</SuccessBanner>}
          </div>
        ) : (
          <>
            <div>
              <label className="label">参加方法</label>
              {data.participationMode === "hybrid" ? (
                <select
                  className="input"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as typeof mode)}
                >
                  <option value="offline">来場参加</option>
                  <option value="online">オンライン参加</option>
                </select>
              ) : (
                <p className="text-sm font-semibold text-tora-ink">
                  {data.participationMode === "online"
                    ? "オンライン参加"
                    : "来場参加"}
                </p>
              )}
            </div>

            {data.options && data.options.length > 0 && (
              <div>
                <label className="label">オプション</label>
                <ul className="space-y-2">
                  {data.options.map((o: EventOptionDTO) => (
                    <li
                      key={o.id}
                      className="text-sm flex items-center gap-2"
                    >
                      <input
                        id={`opt-${o.id}`}
                        type="checkbox"
                        disabled={o.isRequired}
                        checked={selected.has(o.id)}
                        onChange={(e) => {
                          const s = new Set(selected);
                          if (e.target.checked) s.add(o.id);
                          else s.delete(o.id);
                          setSelected(s);
                        }}
                      />
                      <label
                        htmlFor={`opt-${o.id}`}
                        className="flex-1 flex items-center justify-between gap-2"
                      >
                        <span>
                          {o.name}
                          {o.isRequired && (
                            <span className="ml-1 text-xs text-red-600">
                              必須
                            </span>
                          )}
                        </span>
                        {o.priceAmount > 0 && (
                          <span className="font-mono text-tora-ink/70">
                            ¥{o.priceAmount.toLocaleString()}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 事前決済。オンライン決済なら申込後にこの画面で払う。振込なら振込先を出し、
                未登録なら「運営に確認」と出す（黙って何も出さないと、振り込み先が分からないまま申し込ませてしまう） */}
            {data.prepaymentRequired && (
              <div className="rounded-lg border border-amber-400/60 bg-amber-50/50 p-4 space-y-2">
                <p className="text-sm font-semibold">
                  このイベントは事前決済です
                </p>
                {data.paymentMethod === "card" ? (
                  <p className="text-xs text-tora-ink/70">
                    お申し込みの後、この画面でお支払いください。
                  </p>
                ) : (
                  <BankTransferInfo account={data.bankAccount ?? null} lead="お申し込み後、下記へお振り込みください。" />
                )}
              </div>
            )}

            {data.hasSocial && (
              <div className="rounded-lg border border-orange-300/50 bg-orange-50/30 p-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={withSocial}
                    onChange={(e) => setWithSocial(e.target.checked)}
                  />
                  懇親会にも参加する
                </label>
                <div className="text-xs text-tora-ink/60 space-y-0.5">
                  {data.socialFee != null && data.socialFee > 0 && (
                    <p>参加費: ¥{data.socialFee.toLocaleString()}</p>
                  )}
                  {data.socialVenue && <p>会場: {data.socialVenue}</p>}
                  {data.socialStartAt && (
                    <p>開始: {fmtDateTime(data.socialStartAt)}</p>
                  )}
                  {data.socialRemainingCapacity != null && (
                    <p>残席: {data.socialRemainingCapacity}</p>
                  )}
                </div>
              </div>
            )}

            {err && <ErrorBanner>{err}</ErrorBanner>}
            {msg && <SuccessBanner>{msg}</SuccessBanner>}
            <button
              className="btn-primary btn-lg w-full md:w-auto md:px-8"
              onClick={apply}
              disabled={busy || full}
            >
              {busy
                ? "送信中…"
                : full
                  ? "満席のため申込できません"
                  : "申し込む 🎫"}
            </button>
          </>
        )}
      </section>
    </article>
  );
}

/** 振込先（自前の口座）。未登録なら運営に確認してもらう */
function BankTransferInfo({ account, lead }: { account: BankAccount | null; lead: string }) {
  if (!account)
    return (
      <p className="text-xs text-tora-ink/70">
        振込先は準備中です。お手数ですが、運営までお問い合わせください。
      </p>
    );
  return (
    <>
      <p className="text-xs text-tora-ink/70">{lead}</p>
      <dl className="text-sm grid grid-cols-[6em_1fr] gap-x-2 gap-y-1">
        <dt className="text-tora-ink/60">銀行</dt>
        <dd>{account.bankName}</dd>
        <dt className="text-tora-ink/60">支店</dt>
        <dd>{account.branchName}</dd>
        <dt className="text-tora-ink/60">種別・番号</dt>
        <dd>
          {account.accountType} {account.accountNumber}
        </dd>
        <dt className="text-tora-ink/60">名義</dt>
        <dd className="break-words">{account.accountHolder}</dd>
      </dl>
      {account.note && <p className="text-xs text-tora-ink/70">{account.note}</p>}
    </>
  );
}
